import { differenceInMinutes } from 'date-fns';
import { isNil, mapValues, sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import { ConsolidatedSensorDatum, PerSensor, ProcessingSource, RawSensorData } from '@/api/historicalData';
import { Units, convert } from '@/common/convert';
import { CombinedQueryResult } from '@/common/react-query';
import { DateRange } from '@/store';
import { maybeDegradeConsolidatedData } from '@/utils/mockDataHelpers';

export const useTransformQueryResult = <R, D>(
    upstreamResponse: CombinedQueryResult<R>,
    transform: (responseData: R) => D
): CombinedQueryResult<D> => {
    const { data: upstreamData, ...response } = upstreamResponse;

    const transformData = useCallback(
        (data: R | undefined) => (isNil(data) ? undefined : transform(data)),
        [transform]
    );

    const data = useMemo(() => transformData(upstreamData), [transformData, upstreamData]);

    return {
        ...response,
        data,
    };
};

export const convertUnit = <V extends string, D extends { [value in V]: number } & { units: Units }>(
    data: D[],
    valueKey: V,
    targetUnit?: Units
) => {
    if (!targetUnit) return data;

    return data.map((d) => ({
        ...d,
        [valueKey]: convert(d[valueKey]).from(d.units).to(targetUnit),
        units: targetUnit,
    }));
};

export const renameSensorDatumKeys = <K extends string, U extends Units = Units>(
    datum: ConsolidatedSensorDatum,
    sourceKey: string,
    targetKey: K,
    targetUnits?: U
) => {
    type ValueUnitFields = {
        [key in K]: number;
    } & {
        [key in `${K}Units`]: U;
    } & {
        [key in `${K}UnitType`]: string;
    };

    const datumValue = datum.data[sourceKey];

    if (!datumValue) {
        // this is might be a lie if the data field is absent, but preserves old behavior that was a little
        // loose with missing data.
        console.warn('missing expected sensor data field: ', sourceKey);
        return {} as ValueUnitFields;
    }

    if (!targetUnits) {
        return {
            [targetKey]: datumValue.value,
            [`${targetKey}Units`]: datumValue.units,
            [`${targetKey}UnitType`]: datumValue.unitType,
        } as ValueUnitFields;
    }
    return {
        [targetKey]: convert(datumValue.value).from(datumValue.units).to(targetUnits),
        [`${targetKey}Units`]: targetUnits,
        [`${targetKey}UnitType`]: datumValue.unitType,
    } as ValueUnitFields;
};

export const sensorKey = (position: number) => `sensor-${position}`;

/**
 * Arbitrary threshold (in minutes) added to processing interval to determine if a gap is significant.
 * Used to decide when embedded samples should be shown instead of HDR.
 */
const GAP_TOLERANCE_MINUTES = 2;

/**
 * Checks if the time gap between two timestamps exceeds the expected processing interval.
 * Returns true if newerTimestamp is more than (processingIntervalMinutes + GAP_TOLERANCE_MINUTES) ahead of olderTimestamp.
 */
export const exceedsProcessingInterval = (
    olderTimestamp: Date,
    newerTimestamp: Date,
    processingIntervalMinutes: number
): boolean => {
    return differenceInMinutes(newerTimestamp, olderTimestamp) > processingIntervalMinutes + GAP_TOLERANCE_MINUTES;
};

/**
 * Folds the raw rows, one per data type per instant, into one datum per sensor position per instant.
 *
 * Requires `data` in ascending timestamp order: each group keeps the order its rows arrived in, and the gap
 * fillers downstream depend on it. Every accumulator the loop mutates is allocated here, so nothing the
 * caller holds is touched.
 */
export const consolidateBySensorPosition = (data: RawSensorData[]) => {
    const bySensorPosition: PerSensor<Map<string, ConsolidatedSensorDatum>> = {};

    for (const row of data) {
        const sensorPosition = sensorKey(row.sensorPosition);
        const byTimestamp = (bySensorPosition[sensorPosition] ??= new Map());

        let consolidated = byTimestamp.get(row.timestamp);
        if (!consolidated) {
            consolidated = { data: {} } as ConsolidatedSensorDatum;
            byTimestamp.set(row.timestamp, consolidated);
        }

        // first non-empty value across the group wins, as the `acc.x || curr.x` chain it replaced did
        consolidated.latitude ||= row.latitude;
        consolidated.longitude ||= row.longitude;
        consolidated.sensorPosition ||= row.sensorPosition;
        consolidated.sensorConfigChecksum ||= row.configuration_checksum;
        consolidated.timestamp ||= row.timestamp;
        consolidated.data[row.data_type_name] = {
            value: row.value,
            units: row.units,
            unitType: row.unit_type,
        };
    }

    return maybeDegradeConsolidatedData(mapValues(bySensorPosition, (byTimestamp) => [...byTimestamp.values()]));
};

export const combineProcessingSources = <D extends { timestamp: string; processing_source: ProcessingSource }>(
    data: D[] | undefined,
    hdrProcessingIntervalMinutes: number,
    processingSource: ProcessingSource | 'best' = 'best'
) => {
    if (!data?.length) return data;

    if (processingSource !== 'best') {
        return data.filter(({ processing_source }) => processing_source === processingSource);
    }

    // allowing the hdr-embedded gap to be a little closer than the expected hdr interval, so that if we drop one hdr and an intervening embedded
    // is a little closer to one side or the other, we can still keep it,
    const minGapMinutes = hdrProcessingIntervalMinutes - 5;

    const combinedData = [];
    let pendingEmbeddeds = [];
    let lastHdrTimestamp = new Date(0);
    for (const datum of sortBy(data, 'timestamp')) {
        if (datum.processing_source === 'embedded') {
            // buffer embeddeds until we decide whether they're filling a gap
            pendingEmbeddeds.push(datum);
            continue;
        }

        // before adding this hdr, decide if any of the embeddeds we saw since the last hdr are filling a gap
        const nextHdrTimestamp = new Date(datum.timestamp);
        if (pendingEmbeddeds.length) {
            if (exceedsProcessingInterval(lastHdrTimestamp, nextHdrTimestamp, hdrProcessingIntervalMinutes)) {
                // only look for embeddeds to fill gaps if the hdr samples are actually too far apart

                let firstEmbeddedToRetain = 0;
                while (
                    firstEmbeddedToRetain < pendingEmbeddeds.length &&
                    differenceInMinutes(new Date(pendingEmbeddeds[firstEmbeddedToRetain].timestamp), lastHdrTimestamp) <
                        minGapMinutes
                ) {
                    // if the first buffered embedded is close to the HDR before it, skip it
                    firstEmbeddedToRetain++;
                }
                let lastEmbeddedToRetain = pendingEmbeddeds.length - 1;
                while (
                    lastEmbeddedToRetain > -1 &&
                    differenceInMinutes(nextHdrTimestamp, new Date(pendingEmbeddeds[lastEmbeddedToRetain].timestamp)) <
                        minGapMinutes
                ) {
                    // if the last buffered embedded is close to this HDR, skip it
                    lastEmbeddedToRetain--;
                }
                if (firstEmbeddedToRetain <= lastEmbeddedToRetain) {
                    // we have some embeddeds filling a gap!
                    combinedData.push(...pendingEmbeddeds.slice(firstEmbeddedToRetain, lastEmbeddedToRetain + 1));
                }
            }
            pendingEmbeddeds = [];
        }
        // then, push this hdr and reset the embedded buffer
        combinedData.push(datum);
        lastHdrTimestamp = nextHdrTimestamp;
    }

    if (!combinedData.length) {
        // if we never found any HDRs, just use all the embeddeds
        return pendingEmbeddeds;
    }

    // include the tail of pending embeddeds, but skip the beginning if they're too close to the last HDR
    while (
        pendingEmbeddeds.length &&
        differenceInMinutes(new Date(pendingEmbeddeds[0].timestamp), lastHdrTimestamp) < minGapMinutes
    ) {
        pendingEmbeddeds.shift();
    }

    return combinedData.concat(pendingEmbeddeds);
};

/**
 * The input is inclusive because that's how it started and we haven't updated it. Ideally we'd switch to closed-open
 * ranges everywhere, we just haven't gotten to it yet.
 *
 * Generally we just let the inclusive bounds flow throughout the dashboard, including for all query keys. Only pass an argument
 * when constructing the parameters for a specific API entrypoint. Generally older Spotter data entrypoints are exclusive, SM sensor data is inclusive,
 * and some newer device status entrypoints are closed-open. If in doubt, consult the wavefleet implementation.
 */
export type ApiDateBounds = 'inclusive' | 'exclusive' | 'closedOpen';

export const dateRangeParams = (
    inclusiveDateRange: DateRange | null | undefined,
    apiDateBounds: ApiDateBounds = 'inclusive'
) => {
    if (!inclusiveDateRange) {
        return null;
    }

    let { startDate, endDate } = inclusiveDateRange;
    if (apiDateBounds === 'exclusive') {
        startDate = new Date(startDate.getTime() - 1);
        endDate = new Date(endDate.getTime() + 1);
    } else if (apiDateBounds === 'closedOpen') {
        endDate = new Date(endDate.getTime() + 1);
    }

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    };
};

/**
 * Selects the preferred wave data source between HDR and embedded.
 * Always prefers HDR unless embedded is significantly newer (more than one processing interval).
 */
export const selectPreferredWaveSource = <T extends { timestamp: string }>(
    hdrWaves: T | null,
    embeddedWaves: T | null,
    processingRateMins: number = 30
): T | null => {
    if (!hdrWaves && !embeddedWaves) return null;
    if (!embeddedWaves) return hdrWaves;
    if (!hdrWaves) return embeddedWaves;

    const embeddedIsSignificantlyNewer = exceedsProcessingInterval(
        new Date(hdrWaves.timestamp),
        new Date(embeddedWaves.timestamp),
        processingRateMins
    );
    return embeddedIsSignificantlyNewer ? embeddedWaves : hdrWaves;
};
